import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { debounce } from "lodash";

export interface AdsAddressResult {
  /** Inverted full address — building first (e.g. "Lai tn 50, Kesklinna linnaosa, Tallinn, Harju maakond") */
  ipikkaadress: string;
  /** Full address — county first (e.g. "Harju maakond, Tallinn, Kesklinna linnaosa, Lai tn 50") */
  taisaadress: string;
  /** ADS object identifier: EHR building code, apartment code or cadastral unit code. */
  tunnus: string;
  /** WGS84 longitude */
  viitepunkt_l: number;
  /** WGS84 latitude */
  viitepunkt_b: number;
  /** County */
  maakond: string;
  /** Municipality */
  omavalitsus: string;
}

interface AdsApiResponse {
  addresses?: Array<Record<string, unknown>>;
}

interface UseAddressSearchReturn {
  results: AdsAddressResult[];
  isLoading: boolean;
  error: string | null;
  search: (query: string) => void;
  clear: () => void;
}

const ADS_API_URL = "https://inaadress.maaamet.ee/inaadress/gazetteer";
const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 350;
const DEFAULT_RESULTS = 10;

function mapApiResult(raw: Record<string, unknown>): AdsAddressResult {
  return {
    ipikkaadress: String(raw.ipikkaadress ?? ""),
    taisaadress: String(raw.taisaadress ?? ""),
    tunnus: String(raw.tunnus ?? ""),
    viitepunkt_l: Number(raw.viitepunkt_l) || 0,
    viitepunkt_b: Number(raw.viitepunkt_b) || 0,
    maakond: String(raw.maakond ?? ""),
    omavalitsus: String(raw.omavalitsus ?? ""),
  };
}

/**
 * The address string we show in the dropdown and store on the building:
 * building + street first, then city district, city and county. ADS already
 * returns that order in `ipikkaadress`; `taisaadress` is the county-first form
 * and only serves as a fallback when the API omits the inverted one.
 */
export function formatAdsAddress(result: AdsAddressResult): string {
  return result.ipikkaadress || result.taisaadress;
}

/** ADS building identifiers are nine digits; apartment identifiers append a unit suffix. */
export function buildingRegistryFromAddress(result: AdsAddressResult): string | undefined {
  return /^(\d{9})(?:-\d+)?$/.exec(result.tunnus)?.[1];
}

export function useAddressSearch(maxResults = DEFAULT_RESULTS): UseAddressSearchReturn {
  const [results, setResults] = useState<AdsAddressResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchAddresses = useCallback(
    async (query: string) => {
      if (query.length < MIN_QUERY_LENGTH) {
        setResults([]);
        setIsLoading(false);
        return;
      }

      // Cancel any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          address: query,
          results: String(maxResults),
          unik: "1",
        });

        const response = await fetch(`${ADS_API_URL}?${params.toString()}`, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`ADS API error: ${response.status}`);
        }

        const data: AdsApiResponse = await response.json();

        if (!controller.signal.aborted) {
          const mapped = (data.addresses ?? []).map(mapApiResult);
          setResults(mapped);
          setIsLoading(false);
        }
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === "AbortError") {
          return; // Request was cancelled, ignore
        }
        if (!controller.signal.aborted) {
          setError("Failed to fetch addresses");
          setResults([]);
          setIsLoading(false);
        }
      }
    },
    [maxResults]
  );

  const debouncedSearch = useMemo(() => debounce(fetchAddresses, DEBOUNCE_MS), [fetchAddresses]);

  const search = useCallback(
    (query: string) => {
      if (query.length < MIN_QUERY_LENGTH) {
        debouncedSearch.cancel();
        setResults([]);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      debouncedSearch(query);
    },
    [debouncedSearch]
  );

  const clear = useCallback(() => {
    debouncedSearch.cancel();
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setResults([]);
    setIsLoading(false);
    setError(null);
  }, [debouncedSearch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [debouncedSearch]);

  return { results, isLoading, error, search, clear };
}
